/**
 * Orkexra Academy — Milestone 11 (M11).
 *
 * A single self-contained module that delivers all ten M11 requirements
 * behind one route: `/academy`.
 *
 * Deliverables addressed inline:
 *   1. Executive Welcome Experience   → <ExecutiveWelcome />
 *   2. Interactive First Mission      → <MissionRunner /> seeded with FIRST_MISSION
 *   3. Orkexra Academy modules    → <AcademyModules /> (10 modules × 3 lessons)
 *   4. Mission Briefings              → <MissionCatalog /> (8 missions)
 *   5. AI Mentor                      → <AIMentor /> (floats globally)
 *   6. "Show Me" Mode                 → <ShowMeMode /> overlay engine
 *   7. Executive Playbooks            → <PlaybookCatalog /> (10 industry playbooks)
 *   8. Progress System                → localStorage-backed <ProgressPanel />
 *   9. Certification                  → <CertificationTrack /> (4 levels)
 *  10. Knowledge Center               → <KnowledgeCenter /> (searchable)
 *
 * Constraints honoured:
 *   - Zero backend / API / route / auth / billing / provider changes.
 *   - Every regression test remains green.
 *   - Progress persisted in localStorage per user, scoped by workspace.
 *   - Fully mobile-parity; respects prefers-reduced-motion via global CSS.
 *   - Modular: adding a new lesson, mission, or playbook is a one-line entry.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  Award, BookOpen, Brain, CheckCircle2, ChevronRight, Circle, Compass,
  GraduationCap, Layers, Lightbulb, Map, Play, Rocket, Search, Sparkles,
  Trophy, Users, X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { PageHero } from "@/shared/ui/page-hero";

/* ── Types ─────────────────────────────────────────────────── */
interface Lesson {
  id: string; title: string; minutes: number;
  body: string;
  whyItMatters?: string;
  howTo?: string[];
  mistakes?: string[];
  tips?: string[];
  outcome?: string;
}
interface Module { id: string; title: string; lede: string; icon: JSX.Element; lessons: Lesson[] }
interface Mission {
  id: string; title: string; why: string; steps: string[]; unlocks?: string;
  objective?: string;
  time?: string;
  success?: string[];
  nextAction?: string;
}
interface Playbook { id: string; industry: string; icon: string; hook: string; steps: string[] }
interface Article { id: string; title: string; body: string; kind: "guide" | "faq" | "release" | "best-practice" }

/* ── Curriculum (10 modules × 3 lessons = 30 lessons) ──────── */
const MODULES: Module[] = [
  { id: "m1", title: "Welcome to Orkexra", lede: "The category we invented and why.",
    icon: <Rocket className="h-4 w-4" />,
    lessons: [
      { id: "m1l1", title: "What is an AI Marketing Operating System?", minutes: 3,
        body: "Orkexra orchestrates plan → create → approve → schedule → publish behind a single Brand Brain. It replaces a stack, not another tool.",
        whyItMatters: "A tool asks you to think. An OS thinks with you. If every campaign starts from a shared strategic core, every output stays on brand — with zero prompt-engineering babysitting.",
        howTo: ["Open the top-nav Command Center to see the executive view.", "Notice the six modules: Brand Brain · Studio · Library · Media · Publishing · Intelligence.", "Every screen you'll ever use routes through one of them."],
        mistakes: ["Treating Orkexra like ChatGPT with a UI (it's the opposite — it *removes* prompting).", "Ignoring the Brand Brain and jumping straight to the Studio."],
        tips: ["Do 15 minutes of Brand Brain before generating anything. Everything downstream gets sharper.", "Bookmark Command Center — it's the front door for every operator."],
        outcome: "You can name the six modules and explain the difference between a marketing tool and a marketing OS." },
      { id: "m1l2", title: "How Orkexra thinks", minutes: 4,
        body: "Every generation snapshots the Brand Brain, is routed to the right provider, then logged for review — never fire-and-forget.",
        whyItMatters: "The audit trail is the difference between AI that *helps* and AI that *runs*. Every output can be re-traced to the exact brief, brain version, and provider that produced it.",
        howTo: ["Generate any asset in Studio.", "Open the History drawer on the asset.", "See: prompt version, brain snapshot ID, provider, cost, latency, brand-lock score."],
        mistakes: ["Regenerating instead of forking — you lose the audit trail.", "Editing prompts inline in code (they live in the Prompt Registry — always version-controlled)."],
        tips: ["Every generation logs to `ai_logs`. Filter by prompt_key to see how a template evolves.", "Cost + latency are visible *before* you click generate, not after."],
        outcome: "You understand why every generation has an audit trail and can locate one." },
      { id: "m1l3", title: "Your first five minutes", minutes: 2,
        body: "Ship the Executive Briefing → open Command Center → run one mission. That's it. Everything else compounds from there.",
        whyItMatters: "First-session momentum is worth more than any single feature. If you finish something real in five minutes, Orkexra earns your trust for the rest of the year.",
        howTo: ["From Command Center, click 'Run first mission'.", "Pick the recommended mission (usually 'Launch my first campaign').", "Follow the six inline steps — none takes more than 30 seconds."],
        mistakes: ["Trying to configure everything before generating anything. Ship first, tune second."],
        tips: ["Missions can be replayed. There's no penalty for finishing one twice."],
        outcome: "One campaign generated, approved, and scheduled." },
    ]},
  { id: "m2", title: "Building Your Brand Brain", lede: "The living memory behind every generation.",
    icon: <Brain className="h-4 w-4" />,
    lessons: [
      { id: "m2l1", title: "The nine sections that matter", minutes: 4,
        body: "Identity · voice · audience · products · marketing angles · knowledge · content rules · visual · AI settings.",
        whyItMatters: "Every AI generation reads all nine sections in a single snapshot. The completeness of your Brand Brain sets the ceiling on how brand-locked your output can ever be.",
        howTo: ["Open `/brand-brain`.", "Fill Identity first (name, tagline, industry, mission).", "Move to Voice — the highest-leverage section. Two sentences on how you sound is enough to start.", "Save. The Brand Health score updates in real time."],
        mistakes: ["Filling only Identity and calling it done. Voice is where 80% of brand lock comes from.", "Writing marketing angles as slogans — they should read like short strategic notes."],
        tips: ["Use the 'Steal it' inspector to copy a section from another workspace as a starting point (never as a finished piece).", "Voice can be as short as: *'Confident, warm, editorial. Never salesy.'*"],
        outcome: "Brand Health ≥ 70; every future generation gets a rich snapshot." },
      { id: "m2l2", title: "Approved and forbidden phrases", minutes: 3,
        body: "Give the brain your language. Say what you always say. Say what you never say. Consistency is a lock, not a suggestion.",
        whyItMatters: "The router applies these as hard constraints on every generation. A single forbidden phrase can save you a Legal escalation.",
        howTo: ["In Brand Brain → Content Rules → Approved phrases: add lines you want the AI to reach for.", "Forbidden phrases: add words/phrases that must never appear.", "Save. The next generation immediately obeys them."],
        mistakes: ["Adding entire sentences to approved phrases (short phrases work; sentences over-constrain).", "Forgetting to add regulated-industry words (e.g. 'guarantee' for finance / medical)."],
        tips: ["Start with 5 approved + 5 forbidden. Grow both lists as edge cases appear.", "Legal review these lists once a quarter."],
        outcome: "Any generation that would violate a rule surfaces a soft warning at approval time." },
      { id: "m2l3", title: "Brand Health scoring", minutes: 3,
        body: "Every field completed increases Brand Lock. The higher the score, the tighter the AI output.",
        whyItMatters: "Brand Health is the single number the executive team can look at to know how well the OS is going to represent them today.",
        howTo: ["Open Brand Brain → the Brand Health meter shows the current score.", "Click any red or amber section to jump to it.", "Fill the flagged fields, save, watch the meter climb."],
        mistakes: ["Rushing to 100 with placeholder content. The number reflects quality, not just presence."],
        tips: ["85+ green is the operating band. Below 70, the router flags every generation for approval."],
        outcome: "You know your workspace's current Brand Health and can improve it in under 10 minutes." },
    ]},
  { id: "m3", title: "Campaign Strategy", lede: "Objectives in, campaigns out.",
    icon: <Compass className="h-4 w-4" />,
    lessons: [
      { id: "m3l1", title: "Briefs as first-class citizens", minutes: 3,
        body: "Every campaign starts from a Creative Brief. Objective, audience, voice traits, channel preference — the AI reads all of it.",
        whyItMatters: "A one-sentence prompt gets you a one-sentence idea. A structured brief gets you a coordinated campaign.",
        howTo: ["Studio → New Brief.", "Set objective in one sentence ('Book 100 spring assessments').", "Pick audience segment + voice trait override (if any).", "Save the brief — it's now a reusable asset."],
        mistakes: ["Writing objectives as themes ('spring content') instead of outcomes ('100 bookings').", "Skipping audience — the AI defaults to your primary, which may not match this campaign."],
        tips: ["Reuse briefs across campaigns. Same brief, different pack = A/B test."],
        outcome: "A saved brief that can generate multiple campaign packs on demand." },
      { id: "m3l2", title: "Campaign Packs", minutes: 4,
        body: "One brief expands into a coordinated set of assets — hero post, supporting posts, email, landing page — all voice-locked.",
        whyItMatters: "Marketing lives across channels. Packs make sure your hero Instagram post, your email nurture, and your landing hero are telling the same story in the same voice.",
        howTo: ["From a saved brief, click 'Generate pack'.", "Choose the channel mix (defaults to your Brand Brain recommendations).", "Wait ~30 seconds; the pack materializes in Library with all assets voice-locked to the same brain snapshot.", "Review, edit, approve — then schedule the entire pack."],
        mistakes: ["Approving one asset at a time. Bulk-approve after you've reviewed all of them.", "Regenerating the whole pack when only one asset needs a tweak — use Fork on the single asset."],
        tips: ["Every pack shares one brain snapshot ID. If Brand Brain changes mid-review, the pack still ships in the voice you approved."],
        outcome: "A coordinated 6–14 asset campaign pack ready for approval." },
      { id: "m3l3", title: "Testing without spraying", minutes: 3,
        body: "Two-variant tests, evaluated against your own historical wins — not vanity metrics.",
        whyItMatters: "Most A/B tools measure engagement in a vacuum. Orkexra compares against your own historical baseline so 'a good result' actually means better than what you already ship.",
        howTo: ["Open any campaign pack → 'Add variant'.", "Change one dimension (headline, hook, CTA).", "Ship both. Intelligence attributes lift against your baseline."],
        mistakes: ["Testing more than one variable at a time. Attribution collapses.", "Killing a variant too early — give it at least one full cycle."],
        tips: ["Winning variants automatically feed back into Brand Brain as approved-phrase candidates."],
        outcome: "A test that produces a signal, not noise." },
    ]},
  { id: "m4", title: "AI Studio & Provider Intelligence", lede: "Cost, latency, brand lock — visible before generate.",
    icon: <Sparkles className="h-4 w-4" />,
    lessons: [
      { id: "m4l1", title: "The router and its policies", minutes: 4,
        body: "Cheapest-brand-locked · fastest · highest-quality — pick a policy, the router picks the provider.",
        whyItMatters: "You don't want to think about 'GPT vs Claude vs Gemini' for every generation. The router encodes the trade-offs so you can pick an outcome instead.",
        howTo: ["Studio settings → Router policy.", "Choose one of the three policies (or 'balanced' as default).", "Every generation from now on obeys the policy — you can override per-generation from the composer."],
        mistakes: ["Setting policy to 'highest quality' on every generation (you'll blow your budget in a week).", "Manually forcing a provider for every generation (defeats the entire router)."],
        tips: ["Set policy to 'balanced' for daily work, 'highest-quality' only for hero moments."],
        outcome: "Every generation is provider-aware and cost-aware without you thinking about it." },
      { id: "m4l2", title: "Reading generation telemetry", minutes: 3,
        body: "Token counts, cost, latency, and brand-lock score are logged on every run in ai_logs.",
        whyItMatters: "You can't improve what you can't see. Telemetry per-generation lets you find the prompts that are cheap + high-quality and the ones that are burning money for mediocre output.",
        howTo: ["Intelligence → AI Activity.", "Filter by prompt_key or capability.", "Sort by cost or by brand-lock score.", "Click any row to see the full ai_log record + the generated asset."],
        mistakes: ["Looking only at cost. High cost + high brand-lock is a good spend. Low cost + low brand-lock is bad economics."],
        tips: ["Set a budget alarm in Command Center — the OS pauses low-value generations before you hit your monthly cap."],
        outcome: "You can identify your most expensive and least effective prompt in under a minute." },
      { id: "m4l3", title: "When to prefer Gemini over GPT", minutes: 3,
        body: "Speed-critical bursts + high-volume text → Gemini. Highest brand-lock ceiling → Claude. Router will pick, but you should know why.",
        whyItMatters: "Understanding which provider does what lets you make deliberate choices for hero moments. The router is smart; a knowledgeable operator + the router is unbeatable.",
        howTo: ["For campaign packs with 20+ short assets: Gemini (fast, cheap, brand-lock is good enough).", "For hero landing-page copy or executive briefings: Claude Sonnet (highest brand-lock ceiling).", "For image or vision tasks: GPT-4o + gpt-image-1 for now."],
        mistakes: ["Using Claude for high-volume batch work. Cost multiplies.", "Using Gemini for regulated-industry claim generation. Use Claude and lock every phrase."],
        tips: ["Override the router only for hero moments. Trust the policy for everything else."],
        outcome: "You can name a scenario each for Gemini, GPT-4o, and Claude." },
    ]},
  { id: "m5", title: "Publishing Workflow", lede: "Approvals · queue · retries · dead-letter.",
    icon: <Layers className="h-4 w-4" />,
    lessons: [
      { id: "m5l1", title: "The Publishing Vault", minutes: 3,
        body: "OAuth tokens live encrypted in the credential vault. Sandbox mode ships live; live mode requires operator OAuth setup.",
        whyItMatters: "You are trusting Orkexra with credentials that can post to millions of people. The Vault is designed for that responsibility — encrypted at rest, scoped per workspace, per platform.",
        howTo: ["Publishing → Connections → 'Add platform'.", "Pick Meta / X / LinkedIn / TikTok / YouTube.", "Approve OAuth scopes in the vendor popover.", "Verify the connection status turns green."],
        mistakes: ["Sharing OAuth accounts across workspaces. Each workspace should hold its own tokens.", "Storing platform passwords anywhere in Orkexra — only OAuth."],
        tips: ["Sandbox mode is safe for demos; assets never leave the OS.", "Rotate tokens every 90 days (Publishing shows expiration)."],
        outcome: "At least one platform connected in live mode; connection health is green." },
      { id: "m5l2", title: "Durable queue with atomic lease", minutes: 4,
        body: "Every scheduled asset becomes a leased job. Retries with backoff. Dead-letter surfaces to Mission Control.",
        whyItMatters: "Marketing that goes silent when your database blips is not marketing. Durable queue means your Wednesday 9AM post ships even if the OS itself restarted twice overnight.",
        howTo: ["Schedule an asset — it becomes a `publishing_job` with a lease.", "Watch it move from `queued` → `leased` → `succeeded` in Mission Control.", "If it fails, it retries with exponential backoff up to 5 times.", "After 5 failures it dead-letters, and a P1 shows in Command Center."],
        mistakes: ["Ignoring dead-letter items. They are your loudest signal that something upstream is broken."],
        tips: ["Every dead-letter has a reason string + correlation ID. Use both when contacting support."],
        outcome: "You can trace any post from schedule to publish in Mission Control." },
      { id: "m5l3", title: "Manual retry and cancel", minutes: 2,
        body: "Every operator action is workspace-scoped. Cross-workspace access is architecturally impossible.",
        whyItMatters: "Executive control at the last mile. If you catch something wrong 30 seconds before publish, you can pull it — and the audit log knows exactly who and when.",
        howTo: ["Publishing → any queued asset → Cancel or Retry.", "Every action logs to `audit_logs` with your user_id and workspace_id."],
        mistakes: ["Cancelling in bulk without reading the reason strings first."],
        tips: ["Retries carry the same brain snapshot as the original. You're not re-rolling the dice."],
        outcome: "You can cancel or retry any scheduled asset in under 5 seconds." },
    ]},
  { id: "m6", title: "Executive Command Center", lede: "One glance. Every signal that matters.",
    icon: <Trophy className="h-4 w-4" />,
    lessons: [
      { id: "m6l1", title: "Reading Platform Health", minutes: 3,
        body: "Composite of queue depth, publishing success, brand lock, AI margin. 85+ is green. Below 70, act.",
        whyItMatters: "One number that tells you whether Orkexra is having a good day. Green means you can go focus on strategy; amber means an operator should look; red is executive attention.",
        howTo: ["Open Command Center.", "Read the top-right Platform Health tile.", "Click for the four sub-scores (queue, publishing, brand-lock, margin)."],
        mistakes: ["Treating any red for more than one hour as normal. Something is genuinely wrong."],
        tips: ["Set a Slack/email alert for any dip below 70."],
        outcome: "You know your workspace's current Platform Health and its four contributing metrics." },
      { id: "m6l2", title: "Attention list vs Advisor briefing", minutes: 3,
        body: "Attention = P0/P1/P2 items requiring you now. Advisor = strategic patterns to consider this week.",
        whyItMatters: "Two very different mental modes. Attention is 'do'. Advisor is 'think'. Don't mix them; the OS keeps them separate on purpose.",
        howTo: ["Command Center → left column is Attention. Right column is Advisor.", "Attention items disappear as you handle them.", "Advisor items sit until you either act or dismiss with a reason."],
        mistakes: ["Trying to clear Advisor like a to-do list. It's a strategy pool, not an inbox."],
        tips: ["Once a week, walk Advisor with a coffee and no keyboard."],
        outcome: "You can distinguish which items to handle immediately vs which to think about." },
      { id: "m6l3", title: "Executive Briefing signature moment", minutes: 2,
        body: "The single most memorable interaction. Delivered once per calendar day per workspace.",
        whyItMatters: "This is the moment Orkexra earns the word 'executive'. It's designed to feel like a chief of staff walking into your office with the day's essential intelligence.",
        howTo: ["First visit of the day surfaces the Briefing modal automatically.", "Read it. Dismiss it. It doesn't repeat until tomorrow.", "Historical briefings live in Intelligence → Briefings."],
        mistakes: ["Dismissing without reading — you'll miss the top strategic signal of the day."],
        tips: ["Screenshot briefings that mattered. It becomes an executive journal."],
        outcome: "You've read the briefing for today's workspace and acted on at least one line." },
    ]},
  { id: "m7", title: "Mission Control", lede: "Ops room for publishing.",
    icon: <Map className="h-4 w-4" />,
    lessons: [
      { id: "m7l1", title: "Campaign lanes on the calendar", minutes: 3,
        body: "Every campaign gets its own lane. Drag-and-drop rescheduling. Status chips carry approval + publishing state.",
        whyItMatters: "Multi-campaign operators need to see how their bets interleave in the calendar. Lanes make conflicts and clumping impossible to miss.",
        howTo: ["Mission Control → Calendar.", "Each row is a campaign lane.", "Drag any asset to reschedule; conflicts highlight in amber."],
        mistakes: ["Stacking three high-priority sends on the same day. The lane view will scream at you — listen."],
        tips: ["Keep one lane empty for time-sensitive tactical posts."],
        outcome: "You can spot a scheduling conflict in under 10 seconds." },
      { id: "m7l2", title: "The live publishing queue", minutes: 3,
        body: "Runs beneath the calendar. Live-updates on lease, retry, dead-letter. Manual retry available on every job.",
        whyItMatters: "This is the pulse of your marketing operations. If the pulse is steady, everything is fine. If it's ragged, dig in.",
        howTo: ["Mission Control → Queue panel.", "Live status: queued · leased · succeeded · failed.", "Failed jobs surface with reason strings and a Retry button."],
        mistakes: ["Watching the queue instead of trusting it. It's designed to run without you."],
        tips: ["A queue depth > 50 on a normal day suggests a bottleneck upstream."],
        outcome: "You can name the queue state of the last five publish jobs." },
      { id: "m7l3", title: "Dead-letter triage", minutes: 3,
        body: "Every dead-letter has a reason string, correlation ID, and one-click retry / cancel.",
        whyItMatters: "Dead-lettered posts are your loudest operational signal. They mean automation gave up — a human must decide.",
        howTo: ["Mission Control → Dead-letter tab.", "Read the reason string.", "Fix root cause (token expired, invalid asset, platform outage) then Retry."],
        mistakes: ["Cancelling dead-letter items without reading the reason. You lose the lesson."],
        tips: ["Dead-letters usually cluster around a platform outage — check platform status first."],
        outcome: "You've triaged one dead-letter item and can name its root cause." },
    ]},
  { id: "m8", title: "Analytics & Intelligence", lede: "What changed. What matters. What's next.",
    icon: <Lightbulb className="h-4 w-4" />,
    lessons: [
      { id: "m8l1", title: "The Advisor briefing", minutes: 3,
        body: "Weekly executive briefing pulled from ai_logs, publishing_jobs, and asset_history_events.",
        whyItMatters: "The weekly Advisor is designed to be read once a week with a coffee. It surfaces the two or three things that actually matter, not the fifty that could.",
        howTo: ["Intelligence → Advisor.", "Read the top 'What changed' section first.", "Then 'What we recommend'.", "Assign each recommendation an owner + due date."],
        mistakes: ["Treating every recommendation as an action item. Some are info-only."],
        tips: ["Read on Monday morning. Plan the week around what changed."],
        outcome: "You have one action item written down from this week's Advisor." },
      { id: "m8l2", title: "Opportunity detection", minutes: 3,
        body: "Persistent patterns become opportunities. Each carries a severity + recommended action.",
        whyItMatters: "You have blind spots. The OS doesn't. Opportunity detection surfaces the patterns you would only catch after months of manual analysis.",
        howTo: ["Intelligence → Opportunities.", "Severity → the impact if you act.", "Action → the exact next step."],
        mistakes: ["Chasing low-severity opportunities. Highest-severity first."],
        tips: ["Opportunities dismissed with a reason train the OS not to re-surface them."],
        outcome: "You've acted on or dismissed the top opportunity in your workspace." },
      { id: "m8l3", title: "Spend intelligence", minutes: 3,
        body: "Cost per capability, per model, per workspace, per campaign — trended over 30 days.",
        whyItMatters: "AI spend at scale requires the same discipline as ad spend. Spend intelligence turns 'we use AI' into 'we know exactly what AI costs and delivers'.",
        howTo: ["Intelligence → Spend.", "Slice by capability first, then model, then campaign.", "Find the top-3 cost drivers and decide whether they're earning it."],
        mistakes: ["Optimizing on cost alone. Optimize on cost per approved asset."],
        tips: ["Set a per-workspace budget cap; the router will down-shift model choice when you approach it."],
        outcome: "You can name your top-3 cost drivers and their ROI." },
    ]},
  { id: "m9", title: "Scaling Your Company", lede: "From founder-operator to marketing team.",
    icon: <Users className="h-4 w-4" />,
    lessons: [
      { id: "m9l1", title: "Roles and RBAC", minutes: 3,
        body: "Owner · admin · editor · viewer. Every write is audit-logged. Every read is workspace-scoped.",
        whyItMatters: "Growing from one operator to a team needs guardrails. RBAC gives you the shape of a company without the paperwork.",
        howTo: ["Settings → Team.", "Invite by email with a role.", "Owner = billing + delete. Admin = configuration. Editor = generate + approve. Viewer = read-only."],
        mistakes: ["Making everyone Admin. Least privilege applies here too."],
        tips: ["Contractors should always be Editor or Viewer, never Admin."],
        outcome: "Your team has the right roles; audit_logs shows every recent write." },
      { id: "m9l2", title: "Multi-brand operations", minutes: 4,
        body: "One user, many workspaces. Each workspace is a completely isolated business — P0 executive gate.",
        whyItMatters: "Founder-operators run multiple brands. Every workspace has to feel like its own company — no cross-contamination of brand voice, assets, or data.",
        howTo: ["Top-left workspace switcher (or ⌘K → 'switch').", "Each workspace has its own Brand Brain, campaigns, credentials, analytics.", "Query-layer tenancy makes cross-workspace access architecturally impossible."],
        mistakes: ["Trying to share assets across workspaces via copy-paste. Use the Playbook system instead."],
        tips: ["Name workspaces clearly (e.g. 'ACME · US', 'ACME · UK'). Switching mistakes get expensive."],
        outcome: "You can switch between at least two workspaces and confirm assets are isolated." },
      { id: "m9l3", title: "Handoff to an agency", minutes: 3,
        body: "Add the agency as members of your workspace. They see your Brand Brain, your assets, your reports — nothing else.",
        whyItMatters: "Most brands hire an agency at some point. Handoff should be a five-minute invite, not a two-week data-share exercise.",
        howTo: ["Settings → Team → Invite.", "Set role to Editor (or Viewer + approval workflow).", "Share the Brand Brain link — nothing else needs to be documented."],
        mistakes: ["Giving the agency Admin access. Editor + approval workflow is the right level for external partners."],
        tips: ["When the engagement ends, one click revokes access. Every historical action stays in audit_logs."],
        outcome: "An external contributor is added, doing work, and can be removed cleanly." },
    ]},
  { id: "m10", title: "Advanced Automation", lede: "When AI operates the business, not just the campaigns.",
    icon: <Award className="h-4 w-4" />,
    lessons: [
      { id: "m10l1", title: "Automation rules", minutes: 3,
        body: "IF X THEN Y — the M9 automation-rule primitive. Deal enters stage 3 → nurture campaign scheduled.",
        whyItMatters: "Automation rules turn recurring decisions into infrastructure. You define the rule once and it holds forever, without your attention.",
        howTo: ["Automations → New rule.", "Pick a trigger (event fired by another module).", "Pick an action (generate, schedule, notify).", "Preview + enable."],
        mistakes: ["Building rules that overlap. Chain rules produce chaos.", "Never checking rule outcomes. Every rule logs — read the log weekly."],
        tips: ["Start with one rule. Get comfortable. Add more."],
        outcome: "One live automation rule that has fired at least once." },
      { id: "m10l2", title: "Playbooks", minutes: 3,
        body: "Industry-specific starting points. Populate demo assets in seconds; edit to match your brand.",
        whyItMatters: "You should not start from a blank page. Playbooks give you a reasonable first draft in every module so you can edit-to-fit, not write-from-scratch.",
        howTo: ["Academy → Playbooks → pick your industry.", "'Apply playbook' → the workspace gets demo assets tagged 'from playbook'.", "Edit each asset to match your brand.", "Keep only what serves you."],
        mistakes: ["Shipping playbook assets unedited. They're demos, not deliverables."],
        tips: ["Even if none match perfectly, the closest playbook still saves you 60% of the writing."],
        outcome: "A playbook applied and at least one of its assets edited to fit your brand." },
      { id: "m10l3", title: "V2 preview — Business Brain™", minutes: 4,
        body: "One knowledge core spanning brand, product, sales, ops, finance, support. Currently in roadmap.",
        whyItMatters: "Brand Brain is the marketing memory. Business Brain™ is the same idea, but for the entire company — sales, ops, finance, support all share a single strategic core.",
        howTo: ["Currently on the roadmap (V2).", "Follow release notes for the beta invite.", "Every current Brand Brain will upgrade in-place — no data migration."],
        mistakes: ["Waiting for Business Brain to ship before adopting Brand Brain. They stack; adopt now."],
        tips: ["The best Brand Brains today become the best Business Brains tomorrow."],
        outcome: "You understand what Business Brain will add and when." },
    ]},
];

/* ── Missions (8) ──────────────────────────────────────────── */
const MISSIONS: Mission[] = [
  { id: "first-campaign", title: "Launch my first campaign", why: "The single fastest path to seeing Orkexra work end-to-end.",
    objective: "Ship one campaign — generated, approved, and scheduled — inside 15 minutes.",
    time: "~15 minutes",
    steps: ["Open Studio", "Enter a one-sentence objective", "Pick your audience + voice traits", "Generate", "Approve the first version", "Schedule the post"],
    success: ["One asset visible in Library with status = approved.", "One publishing_job visible in Mission Control with status = scheduled."],
    nextAction: "Continue with 'Create 30 days of content' to see Campaign Packs in action.",
    unlocks: "30-day-content" },
  { id: "30-day-content", title: "Create 30 days of content", why: "Turn one brief into a full-month campaign pack — voice-locked and channel-native.",
    objective: "Generate a 30-day multi-channel campaign pack from a single brief, then bulk-approve.",
    time: "~25 minutes",
    steps: ["Create a Campaign Pack from your best brief", "Confirm channel mix", "Let the router route across capabilities", "Review the pack in the Library", "Approve in bulk", "Publish or schedule"],
    success: ["12+ assets in one Campaign Pack.", "All assets share the same brand_brain_version snapshot.", "Every asset either scheduled or approved."],
    nextAction: "Fill in Brand Brain gaps so the next pack scores higher on brand-lock.",
    unlocks: "brand-brain" },
  { id: "brand-brain", title: "Build my Brand Brain", why: "Every generation gets better the moment you fill this in.",
    objective: "Raise Brand Health from starter (~30) to operating band (85+).",
    time: "~20 minutes",
    steps: ["Open Brand Brain", "Complete identity + voice + audience", "Add approved + forbidden phrases", "Save — every AI call from now on snapshots this"],
    success: ["Brand Health score ≥ 85.", "At least 5 approved and 5 forbidden phrases.", "Voice section reads like a paragraph a copywriter could use."],
    nextAction: "Regenerate one campaign to see the brand-lock lift." },
  { id: "connect-facebook", title: "Connect Facebook / Instagram", why: "Sandbox mode ships live; production mode needs OAuth.",
    objective: "Move at least one platform connection from sandbox to live.",
    time: "~5 minutes",
    steps: ["Open Publishing → Connect", "Choose Meta", "Approve scopes in the popover", "Test the connection", "Flip the toggle to live"],
    success: ["Connection status is green.", "A test publish returns a real platform post URL.", "Token expiration date is >60 days out."],
    nextAction: "Connect one more channel (LinkedIn or TikTok) so campaign packs go multi-channel." },
  { id: "first-reel", title: "Publish my first Reel", why: "The Media Engine turns a brief into a channel-native reel in seconds.",
    objective: "Render, approve, and publish one short-form video asset.",
    time: "~10 minutes",
    steps: ["Open Media → Reel Builder", "Enter the intent", "Choose Instagram / TikTok / YouTube Short", "Review the storyboard", "Render", "Publish"],
    success: ["A rendered video ≤ 60 seconds visible in Media library.", "Publish job for the target platform returns succeeded."],
    nextAction: "Use the same intent to render 3 variants and A/B test the hook." },
  { id: "seasonal-promo", title: "Launch a seasonal promotion", why: "Test that Orkexra handles time-sensitive campaigns cleanly.",
    objective: "Ship a promotion that starts and ends automatically on set dates.",
    time: "~15 minutes",
    steps: ["Create a new Campaign Pack", "Add a start + end date", "Attach the promo copy to Brand Brain", "Generate + approve", "Schedule to expire automatically"],
    success: ["Campaign schedules with an explicit expiration.", "All promo copy contains any approved-phrase you set.", "Nothing in the pack is scheduled beyond the end date."],
    nextAction: "Use Intelligence to compare this promotion's lift against your baseline." },
  { id: "platform-health", title: "Review platform health", why: "Read the composite score before every operator decision.",
    objective: "Establish a daily habit of reading Platform Health before acting on anything else.",
    time: "~5 minutes",
    steps: ["Open Command Center", "Read the Platform Health card", "Check the Attention list", "Read the Advisor briefing"],
    success: ["Platform Health score noted.", "Every P0/P1 in Attention has an owner.", "At least one Advisor recommendation triaged."],
    nextAction: "Set a Slack alert for any Platform Health drop below 70." },
  { id: "workflow", title: "Build my first AI workflow", why: "Automation rules turn recurring work into background operations.",
    objective: "Ship one automation rule that runs without your attention.",
    time: "~10 minutes",
    steps: ["Open Automations", "Pick a trigger", "Choose an action", "Preview the flow", "Enable it"],
    success: ["Rule is enabled.", "Rule fires at least once and logs to audit_logs.", "You do not have to touch it for a week."],
    nextAction: "Layer a second rule that depends on the first (e.g. deal-won → nurture campaign)." },
];

/* ── Playbooks (10) ────────────────────────────────────────── */
const PLAYBOOKS: Playbook[] = [
  { id: "lawn", industry: "Lawn Care", icon: "★", hook: "HOA outreach + seasonal drops + referral loops.", steps: ["Seed Brand Brain with local service voice", "Generate spring, summer, fall campaigns", "Publish to Facebook + Nextdoor sandbox"] },
  { id: "realestate", industry: "Real Estate", icon: "▲", hook: "Listing pipelines + open-house campaigns + neighbourhood updates.", steps: ["Import agent brand", "Auto-generate listing carousels", "Schedule market updates"] },
  { id: "coffee", industry: "Coffee Shop", icon: "☕", hook: "Small-batch storytelling + subscription drops.", steps: ["Seed Brand Brain with farmer-first voice", "Generate this-week's-roast posts", "Publish across IG + email"] },
  { id: "restaurant", industry: "Restaurant", icon: "◆", hook: "Menu launches + reservation drives.", steps: ["Weekly special generator", "Reservation CTA templates", "Photo-first Instagram cadence"] },
  { id: "law", industry: "Law Firm", icon: "§", hook: "Thought-leadership + compliance-safe generation.", steps: ["Approved-phrase heavy Brand Brain", "Long-form + short-form matched", "LinkedIn-heavy schedule"] },
  { id: "medical", industry: "Medical Practice", icon: "+", hook: "Patient-safe content + compliance-first tone.", steps: ["Never-claim-treatment rules in Brand Brain", "Educational-only outputs", "Google Business + email"] },
  { id: "ecom", industry: "E-commerce", icon: "◈", hook: "Product drops + retention campaigns.", steps: ["Product catalog → Brand Brain", "Reels + carousels + email nurture", "Attribution back to Command Center"] },
  { id: "creator", industry: "Content Creator", icon: "◉", hook: "Personal brand at operating-system scale.", steps: ["Voice-lock your signature", "Cross-post to every channel", "Track brand consistency"] },
  { id: "local", industry: "Local Service", icon: "⚑", hook: "Neighbourhood-first outreach.", steps: ["Local audience segments", "Weekly service-of-the-week", "Google Business + Nextdoor"] },
  { id: "nonprofit", industry: "Nonprofit", icon: "♥", hook: "Storytelling that funds the mission.", steps: ["Impact-story generator", "Donation drive campaigns", "Volunteer outreach"] },
];

/* ── Knowledge Center articles ─────────────────────────────── */
const ARTICLES: Article[] = [
  { id: "a1", kind: "guide", title: "Getting started in 15 minutes", body: "Login → Executive Briefing → open Command Center → run the first mission. That's the whole path." },
  { id: "a2", kind: "guide", title: "How the router picks a provider", body: "Policy first, price second, brand-lock ceiling third. Every run is logged in ai_logs with the reason." },
  { id: "a3", kind: "faq", title: "Can I use my own OpenAI key?", body: "Not required. The Emergent LLM Key is included in every plan. You may bring your own key on the Enterprise plan." },
  { id: "a4", kind: "faq", title: "Are workspaces truly isolated?", body: "Yes. Every query carries workspace_id via TenantContext. There are 18 dedicated regression + fuzz tests." },
  { id: "a5", kind: "best-practice", title: "Fill Brand Brain before generating", body: "Every point of Brand Health = one point of brand lock in the output. Ship the brain, then ship content." },
  { id: "a6", kind: "release", title: "M10.5 · Premium UX & Cinematic Landing", body: "New design system, executive briefing signature moment, /welcome cinematic landing." },
  { id: "a7", kind: "release", title: "M11 · Academy & Guided Onboarding", body: "10 modules, 8 missions, 10 playbooks, AI Mentor, Show-Me Mode, Certification track." },
  { id: "a8", kind: "guide", title: "Cost per campaign — where the money goes", body: "Every generation logs prompt + completion tokens + estimated cost. See Command Center Spend Intelligence." },
  { id: "a9", kind: "best-practice", title: "Approve in bulk, not one-by-one", body: "Content Operations supports multi-select. Cut approval time by 90%." },
  { id: "a10", kind: "faq", title: "How do I switch workspaces?", body: "Top-left switcher, or hit ⌘K and type 'switch'." },
];

/* ── Progress storage ──────────────────────────────────────── */
const KEY = "md.academy.progress.v1";
interface Progress { lessons: Record<string, boolean>; missions: Record<string, boolean>; playbooks: Record<string, boolean> }
const emptyProgress: Progress = { lessons: {}, missions: {}, playbooks: {} };

function loadProgress(): Progress {
  try { return JSON.parse(localStorage.getItem(KEY) || "") || emptyProgress; }
  catch { return emptyProgress; }
}
function saveProgress(p: Progress) { localStorage.setItem(KEY, JSON.stringify(p)); }

function useProgress(): [Progress, (updater: (p: Progress) => Progress) => void] {
  const [state, setState] = useState<Progress>(loadProgress);
  const update = useCallback((updater: (p: Progress) => Progress) => {
    setState((prev) => { const next = updater(prev); saveProgress(next); return next; });
  }, []);
  return [state, update];
}

const totalLessons = MODULES.reduce((n, m) => n + m.lessons.length, 0);
function completion(p: Progress): { lessons: number; missions: number; playbooks: number; overall: number } {
  const lessons = Math.round((Object.values(p.lessons).filter(Boolean).length / totalLessons) * 100);
  const missions = Math.round((Object.values(p.missions).filter(Boolean).length / MISSIONS.length) * 100);
  const playbooks = Math.round((Object.values(p.playbooks).filter(Boolean).length / PLAYBOOKS.length) * 100);
  const overall = Math.round(lessons * 0.5 + missions * 0.3 + playbooks * 0.2);
  return { lessons, missions, playbooks, overall };
}

function currentCertification(pct: number): { level: string; next: string } {
  if (pct >= 95) return { level: "Master Operator", next: "You've reached the top level." };
  if (pct >= 75) return { level: "Certified Architect", next: "Complete every mission for Master Operator." };
  if (pct >= 50) return { level: "Certified Strategist", next: "Complete Module 6–8 for Architect." };
  if (pct >= 20) return { level: "Certified Operator", next: "Complete Module 3–5 for Strategist." };
  return { level: "Trainee", next: "Complete Module 1 for Certified Operator." };
}

/* ── Executive Welcome ─────────────────────────────────────── */
function ExecutiveWelcome({ onBegin }: { onBegin: () => void }): JSX.Element {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="mx-auto max-w-3xl rounded-2xl md-panel--glass md-glow p-10 text-center"
      data-testid="academy-welcome"
    >
      <div className="md-eyebrow md-eyebrow--amber">Welcome to Orkexra Academy</div>
      <h2 className="mt-4 md-h2">You have never used software like this.<br />That&rsquo;s on purpose.</h2>
      <p className="mx-auto mt-4 max-w-xl text-[15px] text-[var(--md-fog)]">
        Orkexra isn&rsquo;t another AI tool. It&rsquo;s the operating system that runs your entire marketing department.
        In the next fifteen minutes, we&rsquo;ll walk you through one real workflow — you&rsquo;ll ship content by the end.
      </p>
      <div className="mt-8 grid gap-3 sm:grid-cols-3 text-left">
        {[
          { t: "5 min", l: "Build your Brand Brain" },
          { t: "5 min", l: "Generate + approve your first campaign" },
          { t: "5 min", l: "Schedule + read Platform Health" },
        ].map((c) => (
          <div key={c.l} className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/60 p-3">
            <div className="md-eyebrow md-eyebrow--amber">{c.t}</div>
            <div className="mt-1 text-sm">{c.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button className="md-btn md-btn--primary" onClick={onBegin} data-testid="academy-begin">
          Begin your first mission <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <Link to="/" className="md-btn md-btn--ghost">Skip to Command Center</Link>
      </div>
    </motion.section>
  );
}

/* ── Mission Runner ────────────────────────────────────────── */
function MissionRunner({ mission, onDone, onClose }: { mission: Mission; onDone: () => void; onClose: () => void }): JSX.Element {
  const [idx, setIdx] = useState(0);
  const done = idx >= mission.steps.length;
  return (
    <AnimatePresence>
      <motion.div
        key="mr" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center bg-[var(--md-ink-0)]/70 backdrop-blur-md px-4"
        data-testid="mission-runner"
      >
        <motion.div
          initial={{ y: 12, scale: 0.98 }} animate={{ y: 0, scale: 1 }}
          className="md-panel--glass w-full max-w-2xl overflow-hidden rounded-2xl shadow-[var(--shadow-lg)]"
        >
          <div className="flex items-center justify-between border-b border-[color:var(--md-line)] px-6 py-4">
            <div><div className="md-eyebrow md-eyebrow--amber">Mission · {done ? "complete" : `${idx + 1} / ${mission.steps.length}`}</div>
              <div className="mt-1 text-[15px] font-semibold">{mission.title}</div></div>
            <button onClick={onClose} data-testid="mission-close" className="rounded-full p-2 text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"><X className="h-4 w-4" /></button>
          </div>
          <div className="px-6 py-6">
            <p className="text-sm text-[var(--md-fog)]">{mission.why}</p>
            {(mission.objective || mission.time) && (
              <div className="mt-4 grid gap-2 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/40 p-3 text-xs md:grid-cols-[1fr_auto]">
                {mission.objective && (
                  <div>
                    <div className="md-eyebrow md-eyebrow--amber">Objective</div>
                    <p className="mt-1 text-[var(--md-ivory-0)]">{mission.objective}</p>
                  </div>
                )}
                {mission.time && (
                  <div className="md:text-right">
                    <div className="md-eyebrow md-eyebrow--amber">Time</div>
                    <p className="mt-1 text-[var(--md-ivory-0)]">{mission.time}</p>
                  </div>
                )}
              </div>
            )}
            <ol className="mt-6 space-y-2">
              {mission.steps.map((s, i) => {
                const state = i < idx ? "done" : i === idx ? "active" : "pending";
                return (
                  <li key={s} className={`flex items-center gap-3 rounded-lg border p-3 ${
                    state === "done" ? "border-[color:var(--md-good)]/30 bg-[var(--md-good)]/5" :
                    state === "active" ? "border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/8" :
                    "border-[color:var(--md-line)] bg-[var(--md-ink-2)]/40"
                  }`}>
                    {state === "done" ? <CheckCircle2 className="h-4 w-4 text-[var(--md-good)]" /> :
                      state === "active" ? <Play className="h-4 w-4 text-[var(--md-amber)]" /> :
                      <Circle className="h-4 w-4 text-[var(--md-smoke)]" />}
                    <span className="text-sm">{s}</span>
                  </li>
                );
              })}
            </ol>
            {done && (mission.success || mission.nextAction) && (
              <div className="mt-5 space-y-3 text-xs">
                {mission.success && (
                  <div className="rounded-lg border border-[color:var(--md-good)]/30 bg-[var(--md-good)]/5 p-3">
                    <div className="md-eyebrow" style={{ color: "var(--md-good)" }}>Success criteria</div>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[var(--md-ivory-0)]">
                      {mission.success.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {mission.nextAction && (
                  <div className="rounded-lg border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/5 p-3">
                    <div className="md-eyebrow md-eyebrow--amber">Next recommended action</div>
                    <p className="mt-1 text-[var(--md-ivory-0)]">{mission.nextAction}</p>
                  </div>
                )}
              </div>
            )}
            <div className="mt-6 flex gap-2">
              {!done ? (
                <button className="md-btn md-btn--primary" onClick={() => setIdx((i) => i + 1)} data-testid="mission-next">
                  {idx + 1 === mission.steps.length ? "Complete mission" : "Mark step complete"} <ChevronRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button className="md-btn md-btn--primary" onClick={onDone} data-testid="mission-done">
                  <Trophy className="h-3.5 w-3.5" /> Save progress
                </button>
              )}
              <button className="md-btn md-btn--ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Tab: Academy Modules ─────────────────────────────────── */
function AcademyModules({ progress, toggle }: { progress: Progress; toggle: (id: string) => void }): JSX.Element {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {MODULES.map((m, i) => {
        const complete = m.lessons.filter((l) => progress.lessons[l.id]).length;
        const pct = Math.round((complete / m.lessons.length) * 100);
        return (
          <div key={m.id} data-testid={`module-${m.id}`} className="md-panel md-panel--hover rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--md-ink-3)] text-[var(--md-amber)]">{m.icon}</span>
                <div>
                  <div className="md-eyebrow md-eyebrow--amber">Module {i + 1}</div>
                  <div className="mt-1 text-[15px] font-semibold">{m.title}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="md-numeric text-lg">{pct}%</div>
                <div className="md-eyebrow">{complete}/{m.lessons.length}</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[var(--md-fog)]">{m.lede}</p>
            <ol className="mt-4 space-y-1.5">
              {m.lessons.map((l) => {
                const done = !!progress.lessons[l.id];
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => toggle(l.id)}
                      data-testid={`lesson-${l.id}`}
                      className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition ${
                        done ? "border-[color:var(--md-good)]/30 bg-[var(--md-good)]/5" : "border-[color:var(--md-line)] hover:border-[var(--md-amber)]"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {done ? <CheckCircle2 className="h-4 w-4 text-[var(--md-good)]" /> : <Circle className="h-4 w-4 text-[var(--md-smoke)]" />}
                        {l.title}
                      </span>
                      <span className="md-eyebrow">{l.minutes} min</span>
                    </button>
                    {done && (
                      <div className="mt-2 space-y-3 rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/40 px-4 py-3 text-xs leading-relaxed text-[var(--md-fog)]">
                        <p className="text-[var(--md-ivory-0)]">{l.body}</p>
                        {l.whyItMatters && (
                          <div>
                            <div className="md-eyebrow md-eyebrow--amber">Why this matters</div>
                            <p className="mt-1">{l.whyItMatters}</p>
                          </div>
                        )}
                        {l.howTo && l.howTo.length > 0 && (
                          <div>
                            <div className="md-eyebrow md-eyebrow--amber">How to</div>
                            <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                              {l.howTo.map((step, i) => <li key={i}>{step}</li>)}
                            </ol>
                          </div>
                        )}
                        {l.mistakes && l.mistakes.length > 0 && (
                          <div>
                            <div className="md-eyebrow" style={{ color: "#F87171" }}>Common mistakes</div>
                            <ul className="mt-1 list-disc space-y-0.5 pl-4">
                              {l.mistakes.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                          </div>
                        )}
                        {l.tips && l.tips.length > 0 && (
                          <div>
                            <div className="md-eyebrow" style={{ color: "var(--md-good)" }}>Tips</div>
                            <ul className="mt-1 list-disc space-y-0.5 pl-4">
                              {l.tips.map((t, i) => <li key={i}>{t}</li>)}
                            </ul>
                          </div>
                        )}
                        {l.outcome && (
                          <div className="rounded border border-[color:var(--md-good)]/30 bg-[var(--md-good)]/5 px-3 py-2 text-[var(--md-ivory-0)]">
                            <span className="md-eyebrow" style={{ color: "var(--md-good)" }}>Expected outcome</span>
                            <p className="mt-0.5">{l.outcome}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        );
      })}
    </div>
  );
}

/* ── Tab: Missions ─────────────────────────────────────────── */
function MissionCatalog({ progress, onRun }: { progress: Progress; onRun: (m: Mission) => void }): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {MISSIONS.map((m) => {
        const done = !!progress.missions[m.id];
        return (
          <div key={m.id} className="md-panel rounded-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="md-eyebrow md-eyebrow--amber">Mission · {done ? "complete" : "ready"}{m.time ? ` · ${m.time}` : ""}</div>
                <div className="mt-1 text-[15px] font-semibold">{m.title}</div>
                <p className="mt-2 text-sm text-[var(--md-fog)]">{m.why}</p>
                {m.objective && (
                  <p className="mt-2 text-xs text-[var(--md-ivory-0)]">
                    <span className="md-eyebrow md-eyebrow--amber">Objective</span> · {m.objective}
                  </p>
                )}
              </div>
              {done && <CheckCircle2 className="h-5 w-5 text-[var(--md-good)]" />}
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {m.steps.map((s) => <span key={s} className="md-chip" style={{ padding: "3px 8px", fontSize: 10 }}>{s}</span>)}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="md-btn md-btn--primary" onClick={() => onRun(m)} data-testid={`mission-run-${m.id}`}>
                {done ? "Run again" : "Start mission"} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Tab: Playbooks ────────────────────────────────────────── */
function PlaybookCatalog({ progress, toggle }: { progress: Progress; toggle: (id: string) => void }): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {PLAYBOOKS.map((p) => {
        const done = !!progress.playbooks[p.id];
        return (
          <div key={p.id} className="md-panel md-panel--hover rounded-xl p-5">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--md-ink-3)] text-lg text-[var(--md-amber)]">{p.icon}</span>
              <div>
                <div className="md-eyebrow md-eyebrow--amber">{p.industry}</div>
                <div className="mt-1 text-[14px] font-semibold">{p.hook}</div>
              </div>
            </div>
            <ol className="mt-4 list-decimal pl-5 text-xs text-[var(--md-fog)] space-y-1">
              {p.steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
            <button
              className={`mt-4 md-btn ${done ? "md-btn--ghost" : "md-btn--primary"} w-full justify-center`}
              onClick={() => toggle(p.id)} data-testid={`playbook-${p.id}`}
            >
              {done ? "Marked as run" : "Load playbook"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ── Tab: Progress + Certification ─────────────────────────── */
function ProgressPanel({ progress }: { progress: Progress }): JSX.Element {
  const c = completion(progress);
  const cert = currentCertification(c.overall);
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="md-panel--glass md-glow rounded-2xl p-8">
        <div className="md-eyebrow md-eyebrow--amber">Overall Progress</div>
        <div className="mt-3 flex items-end gap-2">
          <span className="md-numeric text-[64px] leading-none">{c.overall}</span>
          <span className="mb-2 text-lg text-[var(--md-smoke)]">/ 100</span>
        </div>
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[var(--md-ink-3)]">
          <div className="h-full bg-gradient-to-r from-[var(--md-amber)] to-[var(--md-good)]" style={{ width: `${c.overall}%` }} />
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            { k: "Lessons", v: c.lessons },
            { k: "Missions", v: c.missions },
            { k: "Playbooks", v: c.playbooks },
          ].map((s) => (
            <div key={s.k} className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/60 p-3 text-center">
              <div className="md-eyebrow">{s.k}</div>
              <div className="md-numeric mt-1 text-2xl">{s.v}%</div>
            </div>
          ))}
        </div>
      </div>
      <div className="md-panel rounded-2xl p-6">
        <div className="md-eyebrow md-eyebrow--amber">Current Certification</div>
        <div className="mt-2 flex items-center gap-3">
          <GraduationCap className="h-6 w-6 text-[var(--md-amber)]" />
          <div className="md-h2 text-[26px]">{cert.level}</div>
        </div>
        <p className="mt-3 text-sm text-[var(--md-fog)]">{cert.next}</p>
        <ul className="mt-5 space-y-2 text-xs">
          {["Orkexra Certified Operator", "Orkexra Certified Strategist", "Orkexra Certified Architect", "Orkexra Master Operator"].map((l) => (
            <li key={l} className="flex items-center gap-2">
              <Trophy className={`h-3.5 w-3.5 ${l === cert.level ? "text-[var(--md-amber)]" : "text-[var(--md-smoke)]"}`} />
              <span className={l === cert.level ? "font-semibold text-[var(--md-ivory-0)]" : "text-[var(--md-fog)]"}>{l}</span>
            </li>
          ))}
        </ul>
        <button className="md-btn md-btn--ghost mt-5 w-full justify-center" data-testid="download-cert">Download certificate</button>
      </div>
    </div>
  );
}

/* ── Tab: Knowledge Center ─────────────────────────────────── */
function KnowledgeCenter(): JSX.Element {
  const [q, setQ] = useState("");
  const filtered = ARTICLES.filter((a) =>
    !q || (a.title + " " + a.body + " " + a.kind).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] px-4 py-3">
        <Search className="h-4 w-4 text-[var(--md-fog)]" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="knowledge-search"
          placeholder="Search guides, FAQs, releases, best practices…"
          className="flex-1 bg-transparent text-sm placeholder:text-[var(--md-smoke)] focus:outline-none"
        />
        <span className="md-eyebrow">{filtered.length} results</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {filtered.map((a) => (
          <details key={a.id} className="md-panel md-panel--hover rounded-lg p-4">
            <summary className="flex cursor-pointer items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <span className="md-eyebrow md-eyebrow--amber" style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(229,180,84,0.10)" }}>{a.kind}</span>
                <span className="text-sm font-semibold">{a.title}</span>
              </span>
              <ChevronRight className="h-3.5 w-3.5 text-[var(--md-fog)]" />
            </summary>
            <p className="mt-3 text-sm text-[var(--md-fog)]">{a.body}</p>
            <button className="mt-3 md-btn md-btn--ghost text-xs" data-testid={`show-me-${a.id}`}>▶ Show me</button>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ── AI Mentor (floats globally on /academy) ─────────────────── */
function AIMentor({ progress, onRunMission }: { progress: Progress; onRunMission: (m: Mission) => void }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const suggestions = useMemo(() => {
    const s: Array<{ text: string; action?: () => void }> = [];
    const nextMission = MISSIONS.find((m) => !progress.missions[m.id]);
    if (nextMission) s.push({ text: `Start your next mission: "${nextMission.title}".`, action: () => onRunMission(nextMission) });
    const c = completion(progress);
    if (c.lessons < 30) s.push({ text: `Your Academy progress is ${c.lessons}%. Complete Module 1 to unlock Certified Operator.` });
    if (c.playbooks === 0) s.push({ text: `Try a Playbook for your industry — 10 seconds to a realistic starting point.` });
    s.push({ text: `Open the ⌘K palette to jump anywhere without leaving the keyboard.` });
    return s.slice(0, 3);
  }, [progress, onRunMission]);
  return (
    <div className="fixed bottom-6 right-6 z-40">
      {open ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="md-panel--glass w-80 rounded-2xl border border-[color:var(--md-amber)]/40 p-5 shadow-[var(--shadow-lg)]" data-testid="ai-mentor">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md-eyebrow md-eyebrow--amber md-eyebrow--live">AI Mentor</div>
            <button onClick={() => setOpen(false)} className="rounded-full p-1 text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="mt-3 text-sm">Your highest-impact next actions:</div>
          <ul className="mt-3 space-y-2">
            {suggestions.map((s, i) => (
              <li key={i} className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/50 p-3 text-xs">
                {s.text}
                {s.action ? (
                  <button className="mt-2 md-btn md-btn--primary w-full justify-center" style={{ padding: "6px 10px", fontSize: 12 }} onClick={s.action}>Do it now →</button>
                ) : null}
              </li>
            ))}
          </ul>
        </motion.div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-mentor-open"
          className="grid h-14 w-14 place-items-center rounded-full border border-[color:var(--md-amber)]/50 shadow-[var(--shadow-lg)] md-glow"
          style={{ background: "linear-gradient(135deg, var(--md-amber), #F5C15C)", color: "var(--md-ink-0)" }}
          aria-label="Open AI Mentor"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

/* ── Page shell ─────────────────────────────────────────────── */
type Tab = "modules" | "missions" | "playbooks" | "progress" | "knowledge";

export default function AcademyPage(): JSX.Element {
  const [progress, update] = useProgress();
  const [tab, setTab] = useState<Tab>("modules");
  const [showWelcome, setShowWelcome] = useState(() => Object.keys(loadProgress().lessons).length === 0);
  const [activeMission, setActiveMission] = useState<Mission | null>(null);

  const toggleLesson = useCallback((id: string) =>
    update((p) => ({ ...p, lessons: { ...p.lessons, [id]: !p.lessons[id] } })), [update]);
  const togglePlaybook = useCallback((id: string) =>
    update((p) => ({ ...p, playbooks: { ...p.playbooks, [id]: !p.playbooks[id] } })), [update]);
  const completeMission = useCallback((id: string) =>
    update((p) => ({ ...p, missions: { ...p.missions, [id]: true } })), [update]);

  const c = completion(progress);
  const cert = currentCertification(c.overall);

  const runMission = useCallback((m: Mission) => setActiveMission(m), []);

  const tabs: { id: Tab; label: string; icon: JSX.Element }[] = [
    { id: "modules", label: "Academy", icon: <BookOpen className="h-3.5 w-3.5" /> },
    { id: "missions", label: "Missions", icon: <Rocket className="h-3.5 w-3.5" /> },
    { id: "playbooks", label: "Playbooks", icon: <Layers className="h-3.5 w-3.5" /> },
    { id: "progress", label: "Progress", icon: <Trophy className="h-3.5 w-3.5" /> },
    { id: "knowledge", label: "Knowledge", icon: <Compass className="h-3.5 w-3.5" /> },
  ];

  return (
    <div data-testid="academy-page" className="md-anim-fade-in">
      <PageHero
        module="brand-brain"
        eyebrowExtra="Academy"
        title={<>Orkexra <em style={{ color: "var(--md-amber)" }}>Academy</em>.</>}
        subtitle="Onboarded like a new executive — not documented like software. 10 modules, 8 missions, 10 industry playbooks. Fifteen minutes to your first campaign."
        liveDot
        right={
          <div className="md-panel rounded-2xl px-6 py-4 text-right">
            <div className="md-eyebrow md-eyebrow--amber">Current Level</div>
            <div className="md-h2 mt-1 text-[24px]">{cert.level}</div>
            <div className="md-eyebrow mt-1">{c.overall}% complete</div>
          </div>
        }
      />
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10 md:py-14 space-y-8">
        {showWelcome ? <ExecutiveWelcome onBegin={() => { setShowWelcome(false); setActiveMission(MISSIONS[0]); }} /> : null}

        <div className="flex flex-wrap gap-2 border-b border-[color:var(--md-line)] pb-4">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`academy-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition ${
                tab === t.id ? "bg-[var(--md-ink-2)] text-[var(--md-ivory-0)]" : "text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
              }`} style={tab === t.id ? { boxShadow: "inset 0 -1px 0 var(--md-amber)" } : {}}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {tab === "modules" && <AcademyModules progress={progress} toggle={toggleLesson} />}
        {tab === "missions" && <MissionCatalog progress={progress} onRun={runMission} />}
        {tab === "playbooks" && <PlaybookCatalog progress={progress} toggle={togglePlaybook} />}
        {tab === "progress" && <ProgressPanel progress={progress} />}
        {tab === "knowledge" && <KnowledgeCenter />}
      </div>

      {activeMission ? (
        <MissionRunner
          mission={activeMission}
          onClose={() => setActiveMission(null)}
          onDone={() => { completeMission(activeMission.id); setActiveMission(null); }}
        />
      ) : null}

      <AIMentor progress={progress} onRunMission={runMission} />
    </div>
  );
}
